import React from 'react'
import {Button} from '@primer/react'
import {IconButtonWithTooltip} from '@github-ui/icon-button-with-tooltip'
import {PaperclipIcon, XIcon} from '@primer/octicons-react'
import {SkeletonBox} from '@primer/react/experimental'
import {useLayoutEffect} from '@github-ui/use-layout-effect'

import invariant from 'tiny-invariant'

const MAX_ATTACHMENT_COUNT = 1
const ACCEPTED_MIME = ['image/*']
// NOTE: There is a string representation of in an error message, if you want to update — check that too
const MAX_ATTACHMENT_SIZE = 1 * 1024 * 1024 // 1MB

const ACCEPTED_MIME_REGEX = ACCEPTED_MIME.map(type => new RegExp(type.replace('*', '.*')))

// MARK: UI

export function PlaygroundChatAttachmentButton(props: {addFiles(files: File[]): void; disabled: boolean}) {
  const inputRef = React.useRef<HTMLInputElement>(null)

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_MIME.join(',')}
        hidden
        onChange={e => {
          props.addFiles(Array.from(e.target.files || []))
        }}
      />
      <IconButtonWithTooltip
        variant="invisible"
        size="small"
        icon={PaperclipIcon}
        aria-label="Attach an image"
        label="Attach an image"
        tooltipDirection="w"
        disabled={props.disabled}
        onClick={e => {
          e.preventDefault()
          inputRef.current?.click()
        }}
      />
    </>
  )
}

export function PlaygroundChatAttachmentDrawer(props: React.PropsWithChildren) {
  return <div className="rounded-top-2 p-3">{props.children}</div>
}

export function PlaygroundChatAttachmentItem(
  props: React.PropsWithChildren<{
    onRemove: () => void
  }>,
) {
  return (
    <div className="rounded-2 p-2" style={{maxWidth: 'min-content'}}>
      <React.Suspense fallback={<Loading />}>
        <div className="d-flex flex-column color-bg-subtle p-1 rounded-2">
          <div className="d-flex flex-items-center" style={{width: '100px', height: '100px'}}>
            {props.children}
          </div>
          <Button
            sx={{color: 'fg.subtle'}}
            variant="invisible"
            trailingVisual={XIcon}
            aria-label="Remove file"
            onClick={props.onRemove}
          >
            Remove
          </Button>
        </div>
      </React.Suspense>
    </div>
  )
}

export function PlaygroundChatAttachmentList(props: React.PropsWithChildren) {
  return <div className="d-flex flex-row gap-3">{props.children}</div>
}

// NOTE: This component may suspend if the file has not loaded yet
function AttachementSlot(props: {file: FileReference}) {
  return (
    <img
      src={props.file.preview}
      alt="attachment"
      style={{
        maxHeight: '75px',
        maxWidth: '100px',
        width: '100%',
        height: '100%',
        objectFit: 'contain',
      }}
    />
  )
}

function Loading() {
  return <SkeletonBox sx={{width: 100, height: 75}} />
}

// MARK: Behaviour

declare global {
  interface HTMLElementEventMap {
    'document:drop': CustomEvent<{transfer: DataTransfer}>
  }
}

export function useChatAttachments() {
  const ref = React.useRef<HTMLDivElement>(null)

  type Actions =
    | {type: 'error'; message: string}
    | {type: 'new_files'; files: File[]}
    | {type: 'remove'; attachment: FileReference}
    | {type: 'reset'}
  type State = {errorMessage?: string; attachments: FileReference[]}
  const [state, dispatch] = React.useReducer(
    (prevState: State, action: Actions): State => {
      switch (action.type) {
        case 'error':
          return {errorMessage: action.message, attachments: []}
        case 'new_files': {
          if (action.files.length === 0) return prevState

          const files = filterUnsupportedFiles(action.files)

          // NOTE: This should probably be passed into this hook, as it may be dynamic based on model.
          if (files.length > MAX_ATTACHMENT_COUNT)
            return {errorMessage: 'Sorry, we only support one file right now.', attachments: []}
          if (files.length === 0)
            return {errorMessage: "Sorry, it look's like that file was not supported.", attachments: []}
          if (areFilesTooLarge(files)) return {errorMessage: 'Sorry, we only support files up to 1MB.', attachments: []}

          return {attachments: action.files.map(f => new FileReference(f))}
        }
        case 'remove':
          return {attachments: prevState.attachments.filter(a => a !== action.attachment)}
        case 'reset':
        default:
          return {attachments: []}
      }
    },
    {attachments: []},
  )

  useLayoutEffect(() => {
    const signal = new AbortController()

    if (ref?.current) {
      const el = ref.current
      // See app/assets/modules/github/behaviors/document-dropzone.ts
      el.classList.add('js-document-dropzone')

      el.addEventListener(
        'document:drop',
        async event => {
          invariant(event.detail?.transfer, 'Expected a transfer')
          const files = Array.from(event.detail.transfer.files || [])
          dispatch({type: 'new_files', files})
        },
        {signal: signal.signal},
      )
    }

    return () => signal.abort()
  }, [])

  const attachments = React.useMemo(() => {
    return state.attachments.map(file => ({
      file,
      remove() {
        dispatch({type: 'remove', attachment: file})
      },
      preview() {
        return <AttachementSlot key={file.key} file={file} />
      },
    }))
  }, [state.attachments])

  return {
    ref,
    errorMessage: state.errorMessage,
    attachments,

    reset() {
      dispatch({type: 'reset'})
    },

    addFiles(files: File[]) {
      dispatch({type: 'new_files', files})
    },
  }
}

function filterUnsupportedFiles(files: File[]): File[] {
  return files.filter(file => ACCEPTED_MIME_REGEX.some(type => file.type.match(type)))
}

function areFilesTooLarge(files: File[]): boolean {
  return files.some(file => file.size > MAX_ATTACHMENT_SIZE)
}

class FileReference {
  constructor(public file: File) {}

  // NOTE: We just use a random UUID, to give React something to indicate this file. Perhaps we should juse the file name?
  // but what if the user uploads mulitple files in different folders, with the same name?
  key = window.crypto.randomUUID()

  #promise?: Promise<string>
  #value?: string // BASE64 DataURI

  get base64URI() {
    if (this.#value) return this.#value
    if (this.#promise) return this.#promise

    this.#promise = new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const value = reader.result as string
        resolve(value)
        this.#value = value
      }
      reader.onerror = reject
      reader.readAsDataURL(this.file)
    })

    return this.#promise
  }

  get preview() {
    // NOTE: In the future, if the file is not an image, perhaps we can return a generic preview image
    if (this.#value) return this.#value

    // We throw to suspend the component
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw this.base64URI
  }
}

try{ ACCEPTED_MIME_REGEX.displayName ||= 'ACCEPTED_MIME_REGEX' } catch {}
try{ PlaygroundChatAttachmentButton.displayName ||= 'PlaygroundChatAttachmentButton' } catch {}
try{ PlaygroundChatAttachmentDrawer.displayName ||= 'PlaygroundChatAttachmentDrawer' } catch {}
try{ PlaygroundChatAttachmentItem.displayName ||= 'PlaygroundChatAttachmentItem' } catch {}
try{ PlaygroundChatAttachmentList.displayName ||= 'PlaygroundChatAttachmentList' } catch {}
try{ AttachementSlot.displayName ||= 'AttachementSlot' } catch {}
try{ Loading.displayName ||= 'Loading' } catch {}